import type {
  DocumentUploadRenderedComponent,
  DocumentUploadSdkConfig,
  DocumentUploadError,
  Maybe,
} from "@highnoteplatform/types";

export type { DocumentUploadError };

import { buildComponents } from "./buildComponents";
import { generateDocumentUploadRequestError, mapError } from "./errorHandler";
import { verifyDocumentUploadComponentConfig } from "./config";
import { endDocumentUploadSession } from "./createInnerDom";

const initializeSdk = async (config: Readonly<DocumentUploadSdkConfig>) => {
  const { onError, onLoad, documentUploadComponent } = config;
  const { selector } = documentUploadComponent;
  const container = document.querySelector(selector);
  if (container) {
    try {
      container.innerHTML = "";
      const component = await buildComponents(config);
      if (component) {
        container.append(component);
        onLoad(component);
      }
    } catch (error: unknown) {
      mapError(error, onError);
    }
  } else {
    mapError(
      generateDocumentUploadRequestError("Invalid document upload component."),
      onError
    );
  }
};

const initializeDocumentUploadSdk = async (
  config: Readonly<DocumentUploadSdkConfig>
): Promise<Maybe<DocumentUploadRenderedComponent>> => {
  if (verifyDocumentUploadComponentConfig(config)) {
    try {
      await initializeSdk(config);
    } catch (error: unknown) {
      mapError(error, config.onError);
    }

    return {
      unmount: () => {
        const {
          documentUploadComponent: { selector },
        } = config;
        const container = document.querySelector(selector);
        if (container !== null) {
          container.replaceChildren("");
        }
      },

      endSession: async () => await endDocumentUploadSession(config),
    };
  }

  return undefined;
};

export { initializeDocumentUploadSdk };
